// conversions.ts, not registry.ts: this module must stay free of the
// Highcharts renderer imports so the builder (and its tests) can load it.
import { getConvertibleTargets } from "src/charts/conversions";

/**
 * Per-widget display config — the settings a dashboard author can pin on a
 * widget instance from the builder's gear menu, saved per dashboard widget.
 *
 * This file is the single declaration of *which* settings each chart type
 * supports. It drives two things at once:
 *
 *   1. the builder's gear menu (a chart with no settings gets no gear), and
 *   2. validation of whatever comes back from the API.
 *
 * On save these go to `config_override` on PATCH /dashboards/{code}/layout.
 * On load the backend returns them as `config_override` on each widget of
 * GET /dashboards/{code} (it also merges them into `chart_spec`, which is why
 * every read here is per-key and tolerant — see resolveWidgetConfig).
 */

// ─── Value shape ──────────────────────────────────────────────────────────────

export type WidgetInstanceConfig = {
  /** Which tab a tabbed widget opens on. A payload *key*, not a label. */
  default_tab?: string;
  /** Sparkle only: start with the sparkline collapsed. */
  hide_line_chart?: boolean;
  /** Render as this chart type instead of the widget's own (D3/D4). */
  chart_type?: string;
};

type WidgetConfigKey = keyof WidgetInstanceConfig;

// ─── Field declarations ───────────────────────────────────────────────────────

/**
 * How the gear menu renders one setting:
 *   toggle — acts immediately, no submenu
 *   select — hover opens a submenu of `options`
 *   tabs   — hover opens a submenu built from the widget's *payload* at
 *            runtime, because the tab keys are data (sparkle's segments are
 *            `all` / `medical_precert` / `pharmacy_pa`) and can't be listed here
 */
type WidgetConfigControl = "toggle" | "select" | "tabs";

export type WidgetConfigOption = { value: string; label: string };

export type WidgetConfigField = {
  key: WidgetConfigKey;
  label: string;
  control: WidgetConfigControl;
  /** Static choices for `select`. Derived per chart type, hence a function. */
  options?: (chartType: string) => WidgetConfigOption[];
};

// Tab keys are payload-defined, so all we can assert is "a non-empty string".
// A key that no longer exists in the payload is not an error: SparkleWidget,
// GroupedBarWidget and FunnelWidget all fall back to their first available
// tab, so a stale default degrades to the default view rather than breaking.
const tabField = (label: string): WidgetConfigField => ({
  key: "default_tab",
  label,
  control: "tabs",
});

const hideLineChartField: WidgetConfigField = {
  key: "hide_line_chart",
  label: "Hide Line Chart",
  control: "toggle",
};

// "Set Default View" (D3/D4). The legal targets are NOT listed here — they
// come from CHART_CONVERSIONS via getConvertibleTargets, which stays the one
// place that knows bar → [pie, single_bar, table] and that grouped_bar
// converts to nothing.
const defaultViewField: WidgetConfigField = {
  key: "chart_type",
  label: "Set Default View",
  control: "select",
  options: (chartType) =>
    getConvertibleTargets(chartType).map((target) => ({
      value: target,
      label: CHART_TYPE_LABELS[target] ?? target,
    })),
};

const CHART_TYPE_LABELS: Record<string, string> = {
  bar: "Bar",
  pie: "Pie",
  single_bar: "Single Bar",
  table: "Table",
};

/**
 * Chart types with display settings of their own. `chart_type` is not listed
 * here — it is added automatically below for any chart that has conversions,
 * so adding a conversion to the registry is all it takes to get the option.
 */
const DISPLAY_SETTINGS: Record<string, WidgetConfigField[]> = {
  // Only these two render a real tab strip (both via SegmentTabs, driven by a
  // segmented payload). Funnel is deliberately absent: its analytics panel is
  // a secondary sub-panel, not widget tabs, and it only appears when the
  // payload carries `analytics` at all.
  sparkle: [tabField("Set Default Tab"), hideLineChartField],
  grouped_bar: [tabField("Set Default Tab")],
};

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === "string" && value.length > 0;

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Every setting this chart type supports. An empty array means the builder
 * shows no gear button at all — which is the common case, since 23 chart
 * types never reach ChartFactory and 13 have no conversions.
 */
export function getWidgetConfigFields(chartType: string): WidgetConfigField[] {
  const fields = DISPLAY_SETTINGS[chartType] ?? [];
  // CHART_CONVERSIONS is the only gate: a chart type with no declared targets
  // gets no "Set Default View". Conversions that can't actually be applied
  // are removed there rather than special-cased here.
  const canConvert = getConvertibleTargets(chartType).length > 0;
  return canConvert ? [...fields, defaultViewField] : fields;
}

export function hasWidgetConfigFields(chartType: string): boolean {
  return getWidgetConfigFields(chartType).length > 0;
}

/**
 * Read path: pull this widget's display config out of whatever the API sent.
 *
 * Deliberately per-key and non-throwing. `source` is often the widget's
 * `chart_spec`, which is the *query* spec — live payloads look like
 * `{source_table, metric, column, filters, ...}` — with display keys merged
 * in by the backend. So we pick out only the keys this chart supports, and
 * one malformed key must not discard the others.
 */
export function resolveWidgetConfig(
  chartType: string,
  source: unknown,
): WidgetInstanceConfig {
  if (source === null || typeof source !== "object" || Array.isArray(source)) {
    return {};
  }

  const raw = source as Record<string, unknown>;
  const offered = new Set(getWidgetConfigFields(chartType).map((f) => f.key));
  const config: WidgetInstanceConfig = {};

  if (offered.has("default_tab") && isNonEmptyString(raw.default_tab)) {
    config.default_tab = raw.default_tab;
  }

  if (offered.has("hide_line_chart") && typeof raw.hide_line_chart === "boolean") {
    config.hide_line_chart = raw.hide_line_chart;
  }

  // A "convert to itself" override is a no-op, so it is dropped rather than
  // stored where it could shadow a later template change.
  if (
    offered.has("chart_type") &&
    isNonEmptyString(raw.chart_type) &&
    raw.chart_type !== chartType &&
    getConvertibleTargets(chartType).includes(raw.chart_type)
  ) {
    config.chart_type = raw.chart_type;
  }

  return config;
}

/**
 * Write path. Always returns a complete object — including an empty one —
 * because clearing a setting has to overwrite the stored value, not be
 * omitted from the payload.
 */
export function toConfigOverride(
  config: WidgetInstanceConfig,
): Record<string, unknown> {
  const payload: Record<string, unknown> = {};
  if (config.default_tab !== undefined) payload.default_tab = config.default_tab;
  if (config.hide_line_chart !== undefined) {
    payload.hide_line_chart = config.hide_line_chart;
  }
  if (config.chart_type !== undefined) payload.chart_type = config.chart_type;
  return payload;
}
